import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Bell, Gavel, ReceiptText, MessageSquare, Package, Wallet, Star, CheckCheck, Loader2, ShieldCheck, ShieldAlert, Building2,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n, type Lang } from "../lib/i18n";
import { Button } from "./beui/Button";
import { MorphPopover, MorphPopoverContent, MorphPopoverTrigger } from "./motion/popover-morph";

// Several events share a notification type (offers, signatures, applications).
// Match their stored titles so a pending or declined event cannot read as accepted.
const TITLE_KEYS = new Map<string, string>([
  ["milestone released — you were paid", "notifications.events.milestoneReleased"],
  ["contract completed", "notifications.events.contractCompleted"],
  ["escrow funded", "notifications.events.escrowFunded"],
  ["contract fully signed", "notifications.events.contractFullySigned"],
  ["order cancelled", "notifications.events.orderCancelled"],
  ["professional verification approved", "notifications.events.professionalApproved"],
  ["new order received", "notifications.events.orderReceived"],
  ["purchase confirmed", "notifications.events.purchaseConfirmed"],
  ["new progress update", "notifications.events.progressUpdate"],
  ["worker logged time", "notifications.events.workerLoggedTime"],
  ["offer accepted", "notifications.events.offerAccepted"],
  ["project offer accepted and signed", "notifications.events.projectOfferAccepted"],
  ["you're hired", "notifications.events.hired"],
  ["new bid on your project", "notifications.events.bidReceived"],
  ["new review", "notifications.events.reviewReceived"],
  ["your bid was awarded", "notifications.events.bidAwarded"],
  ["new counter-offer", "notifications.events.counterOffer"],
]);

function notificationTitle(title: string, lang: Lang, t: (key: string, fallback?: string) => string): string {
  if (lang !== "tl") return title;
  const normalized = title.trim().replace(/^🎉\s*/, "").replace(/!$/, "").toLowerCase();
  const key = TITLE_KEYS.get(normalized);
  if (key) return t(key, title);
  // Review notices include a rating; keep it visible alongside the translated title.
  const review = /^new ([1-5])★ review$/.exec(normalized);
  if (review) return `${t("notifications.events.reviewReceived")} (${review[1]}★)`;
  return title;
}

const ICONS: Record<string, any> = {
  bid_received: Gavel, bid_shortlisted: Gavel, bid_awarded: Gavel, bid_negotiation: Gavel,
  quote_received: ReceiptText, quote_shortlisted: ReceiptText, quote_awarded: ReceiptText,
  message: MessageSquare,
  project_question: MessageSquare,
  order_placed: Package, order_fulfilled: Package, order_completed: Package, order_cancelled: Package,
  escrow_funded: Wallet, milestone_released: Wallet,
  review_received: Star,
  verification_approved: ShieldCheck, verification_rejected: ShieldCheck,
  dispute_opened: ShieldAlert, dispute_responded: ShieldAlert, dispute_resolved: ShieldAlert,
  company_invite: Building2, company_joined: Building2,
};

function ago(iso: string): string {
  const then = Date.parse(iso);
  if (!then) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

export function NotificationBell() {
  const { lang, t } = useI18n();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Lazy imports of api to keep this component light on first paint
  async function loadCount() {
    try { setUnread(await api.notifUnreadCount()); } catch { /* ignore */ }
  }
  async function loadList() {
    setBusy(true);
    try { const r = await api.getNotifications(); setItems(r?.notifications ?? []); setUnread(r?.unread ?? 0); }
    catch { setItems([]); }
    finally { setBusy(false); }
  }

  // Poll unread count; also refresh on route change.
  const [loc] = useLocation();
  useEffect(() => { loadCount(); }, [loc]);
  useEffect(() => {
    const h = setInterval(loadCount, 25000);
    const onFocus = () => loadCount();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(h); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, []);

  function changeOpen(next: boolean) {
    setOpen(next);
    if (next) void loadList();
    else ref.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }

  async function openItem(n: any) {
    setOpen(false);
    if (!n.read) {
      try { const r = await api.markNotifRead(n.id); setUnread(r?.unread ?? Math.max(0, unread - 1)); } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  }
  async function markAll() {
    try { await api.markAllNotifRead(); setUnread(0); setItems((prev) => (prev ? prev.map((x) => ({ ...x, read: true })) : prev)); } catch { /* ignore */ }
  }

  return (
    <div className="relative" ref={ref}>
      <MorphPopover open={open} onOpenChange={changeOpen}>
      <MorphPopoverTrigger><Button variant="ghost" size="icon" pressScale={0.94} className="btn-ghost h-9 w-9 p-0 relative" title={t("notifications.title")} aria-label={t("notifications.title")}>
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[10px] font-bold leading-none grid place-items-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button></MorphPopoverTrigger>

      <MorphPopoverContent align="end" className="w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-line bg-white p-0 overflow-hidden" onOpenAutoFocus={panel => panel.querySelector<HTMLButtonElement>("button")?.focus()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span className="font-bold text-navy">{t("notifications.title")}</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-brand-red font-semibold inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" />{t("notifications.markAllRead")}</button>}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {busy && !items ? (
              <div className="py-10 text-center text-mut"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : !items || items.length === 0 ? (
              <div className="py-10 text-center text-mut text-sm">
                <Bell className="w-7 h-7 mx-auto mb-2 opacity-40" />
                {t("notifications.empty")}
              </div>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button key={n.id} onClick={() => openItem(n)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-line hover:bg-navy-50 ${n.read ? "" : "bg-brand-red/5"}`}>
                    <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${n.read ? "bg-navy-50 text-navy-600" : "bg-brand-red/10 text-brand-red"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-navy truncate">{notificationTitle(n.title, lang, t)}</span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-brand-red shrink-0" />}
                      </div>
                      {n.body && <p className="text-xs text-mut mt-0.5 line-clamp-2">{n.body}</p>}
                      <span className="text-[11px] text-mut">{ago(n.createdAt)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
      </MorphPopoverContent>
      </MorphPopover>
    </div>
  );
}
